import { Inject, Injectable, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material';
import * as moment from 'moment';
import { Moment } from 'moment';

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
	const valuesArray = Array(length);
	for (let i = 0; i < length; i++) {
		valuesArray[i] = valueFunction(i);
	}
	return valuesArray;
}


/** Adapts Moment.js Dates for use with Angular Material. */
@Injectable()
export class AzcDateAdapter extends DateAdapter<Moment> {
	// Note: all of the methods that accept a `Moment` input parameter immediately call `this.clone`
	// on it. This is to ensure that we're working with a `Moment` that has the correct locale setting
	// while avoiding mutating the original object passed to us. Just calling `.locale(...)` on the
	// input would mutate the object.

	private _localeData: {
		firstDayOfWeek: number,
		longMonths: string[],
		shortMonths: string[],
		dates: string[],
		longDaysOfWeek: string[],
		shortDaysOfWeek: string[],
		narrowDaysOfWeek: string[]
	};

	constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
		super();
		this.setLocale(dateLocale || moment.locale());
	}

	setLocale(locale: string) {
		super.setLocale(locale);
		const momentLocaleData = moment.localeData(locale);
		this._localeData = {
			firstDayOfWeek: momentLocaleData.firstDayOfWeek(),
			longMonths: momentLocaleData.months(),
			shortMonths: momentLocaleData.monthsShort(),
			dates: range(31, (i) => this.createDate(2017, 0, i + 1).format('D')),
			longDaysOfWeek: momentLocaleData.weekdays(),
			shortDaysOfWeek: momentLocaleData.weekdaysShort(),
			narrowDaysOfWeek: momentLocaleData.weekdaysMin(),
		};
	}

	getYear(date: Moment): number {
		return this.clone(date).year();
	}

	getMonth(date: Moment): number {
		return this.clone(date).month();
	}

	getDate(date: Moment): number {
		return this.clone(date).date();
	}

	getDayOfWeek(date: Moment): number {
		return this.clone(date).day();
	}

	getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
		// Moment.js doesn't support narrow month names, so we just use short if narrow is requested.
		return style === 'long' ? this._localeData.longMonths : this._localeData.shortMonths;
	}

	getDateNames(): string[] {
		return this._localeData.dates;
	}

	getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
		if (style === 'long') {
			return this._localeData.longDaysOfWeek;
		}
		if (style === 'short') {
			return this._localeData.shortDaysOfWeek;
		}
		return this._localeData.narrowDaysOfWeek;
	}

	getYearName(date: Moment): string {
		return this.clone(date).format('YYYY');
	}

	getFirstDayOfWeek(): number {
		return this._localeData.firstDayOfWeek;
	}

	getNumDaysInMonth(date: Moment): number {
		return this.clone(date).daysInMonth();
	}

	clone(date: Moment): Moment {
		return date.clone().locale(this.locale);
	}

	createDate(year: number, month: number, date: number): Moment {
		// Moment.js will create an invalid date if any of the components are out of bounds, but we
		// explicitly check each case so we can throw more descriptive errors.
		if (month < 0 || month > 11) {
			throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
		}

		if (date < 1) {
			throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
		}

		const result = moment({ year, month, date }).locale(this.locale);

		// If the result isn't valid, the date must have been out of bounds for this month.
		if (!result.isValid()) {
			throw Error(`Invalid date "${date}" for month with index "${month}".`);
		}
		return result;
	}

	today(): Moment {
		return moment().locale(this.locale);
	}

	parse(value: any, parseFormat: string | string[]): Moment | null {
		if (value && typeof value === 'string') {
			return moment(value, parseFormat, this.locale);
		}
		return value ? moment(value).locale(this.locale) : null;
	}

	format(date: Moment, displayFormat: string): string {
		date = this.clone(date);
		if (!this.isValid(date)) {
			throw Error('MomentDateAdapter: Cannot format invalid date.');
		}
		return date.format(displayFormat);
	}

	addCalendarYears(date: Moment, years: number): Moment {
		return this.clone(date).add({ years });
	}

	addCalendarMonths(date: Moment, months: number): Moment {
		return this.clone(date).add({ months });
	}

	addCalendarDays(date: Moment, days: number): Moment {
		return this.clone(date).add({ days });
	}

	toIso8601(date: Moment): string {
		return this.clone(date).format();
	}

	fromIso8601(iso8601String: string): Moment | null {
		const d = moment(iso8601String, moment.ISO_8601).locale(this.locale);
		return this.isValid(d) ? d : null;
	}

	isDateInstance(obj: any): boolean {
		return moment.isMoment(obj);
	}

	isValid(date: Moment): boolean {
		return this.clone(date).isValid();
	}

	invalid(): Moment {
		return moment({ day: 32, month: 13, year: -1 });
	}
}
